import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@trillion/ui/components/ui/button";

interface CopyableCodeProps {
  label: string;
  code: string;
}

export function CopyableCode({ label, code }: CopyableCodeProps) {
  const [feedback, setFeedback] = useState("");
  const copied = feedback.startsWith("Copied");

  async function copyCode() {
    try {
      if (typeof navigator.clipboard?.writeText !== "function") {
        throw new Error("Clipboard API unavailable");
      }

      await navigator.clipboard.writeText(code);
      setFeedback(`Copied ${label}.`);
    } catch {
      setFeedback("Copy failed. Select and copy the code manually.");
    }
  }

  return (
    <div className="min-w-0 space-y-2">
      <div className="relative min-w-0 rounded-lg border bg-muted/50">
        <pre
          aria-label={label}
          className="overflow-x-auto p-4 pr-14 text-sm leading-relaxed"
          tabIndex={0}
        >
          <code>{code}</code>
        </pre>
        <Button
          aria-label={`Copy ${label}`}
          className="absolute right-2 top-2"
          onClick={() => void copyCode()}
          size="icon-sm"
          type="button"
          variant="outline"
        >
          {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
        </Button>
      </div>
      <p aria-live="polite" className="min-h-5 text-sm text-muted-foreground" role="status">
        {feedback}
      </p>
    </div>
  );
}
